/**
 * Survivor Record panel.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* z-index 1001 — the same layer every other full-screen panel uses, NOT the 15
   this carried until 2026-09-18. At 15 the Record rendered under the other
   panels' scrims, so opening the shop and then pressing I put the bag on
   screen, dimmed, under the shop. It is a window and it now says so: see the
   isModal note in ui/panels.ts, which is what stops the two coexisting in
   the first place.

   No left padding any more, either. It used to be padded to centre the panel
   in the space the always-on-top player frame did not occupy; the frame now
   sits BEHIND this scrim (hud.css.ts), so there is nothing to dodge and the
   Record centres on the viewport like every other window. Janeks' call. */
.bv-paperdoll {
  position: absolute; inset: 0; z-index: 1001;
  display: flex; align-items: center; justify-content: center;
  /* Matches .bv-scrim (window.css.ts) exactly. This was 0.55 — the one panel
     that predates the shared shell and never got its number. It did not show
     while the only thing under the dim was the world; now that the player
     frame is under it too, a lighter dim reads as the menu NOT having gone
     behind anything. Verified by hit test, not by eye: elementFromPoint over
     the player card and the action bar returns this scrim. */
  background: rgba(6,4,2,0.78);
}
.bv-paperdoll[hidden] { display: none; }
.bv-pd-panel {
  /* Wider since the record became two blocks beside the preview (2026-09-16):
     at 920px the two 5-wide slot grids and the worn slots shared 600px and the
     hideout grid ran out of room before its first full row. */
  width: min(1120px, 96%); max-height: 90%; overflow: auto;
  background: linear-gradient(165deg, var(--panel-a), var(--panel-b));
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  box-shadow: 0 16px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05);
  font-family: var(--font-technical); color: var(--bone);
}
.bv-pd-header {
  display: flex; align-items: center; justify-content: space-between;
  background: linear-gradient(90deg, #3a2717, var(--panel-a));
  border-bottom: 1px solid var(--line); padding: 14px 18px;
  font: 700 20px/1 var(--font-display);
  text-transform: uppercase; letter-spacing: 0.06em; color: var(--brass-hot);
}
/* Fixed left column (the survivor + their slots) against a flexible tabbed
   right column. Left is a fixed px width rather than a fraction because it
   holds a 3D viewport with a framed camera — letting it flex would reframe the
   model every time the panel resized. */
.bv-pd-body { display: grid; grid-template-columns: 260px 1fr; gap: 18px; padding: 18px; align-items: start; }
@media (max-width: 720px) { .bv-pd-body { grid-template-columns: 1fr; } }
.bv-pd-left { display: flex; flex-direction: column; gap: 12px; }
.bv-pd-preview {
  position: relative; aspect-ratio: 3 / 4; border-radius: var(--radius-well); overflow: hidden;
  border: 1px solid var(--line);
  /* Vignette so a dark-clothed survivor still separates from the panel, and so
     the empty box is deliberate-looking if WebGL is unavailable. */
  background: radial-gradient(ellipse at 50% 38%, #2a1e14 0%, #150e09 70%, var(--well) 100%);
  box-shadow: inset 0 0 24px rgba(0,0,0,0.6);
  touch-action: none; /* the canvas handles drag-to-turn via pointer events */
}
.bv-cp-canvas { position: absolute; inset: 0; width: 100%; height: 100%; cursor: grab; }
.bv-cp-canvas:active { cursor: grabbing; }
/* Flavour rather than instruction — the typewriter face, like the Codex's
   own notes and specimen labels. */
.bv-pd-preview-hint {
  position: absolute; bottom: 6px; left: 0; right: 0; text-align: center;
  font: 10px var(--font-note); text-transform: uppercase;
  letter-spacing: 0.12em; color: var(--bone-faint); pointer-events: none;
}
.bv-pd-right { display: flex; flex-direction: column; min-width: 0; }
.bv-pd-tabs { display: flex; gap: 4px; border-bottom: 1px solid var(--line); margin-bottom: 12px; }
.bv-pd-tab {
  background: none; border: 1px solid transparent; border-bottom: none;
  border-radius: var(--radius-well) var(--radius-well) 0 0; padding: 7px 14px; cursor: pointer;
  font: 700 13px var(--font-display); text-transform: uppercase;
  letter-spacing: 0.1em; color: var(--bone-dim);
}
.bv-pd-tab:hover { color: var(--brass-bright); }
.bv-pd-tab-on {
  color: var(--brass-hot); background: var(--well);
  border-color: var(--line); margin-bottom: -1px; padding-bottom: 8px;
}
/* Unspent points are the one thing in this panel that rots if ignored, and the
   Inventory tab is the one that opens by default — so the badge has to pull. */
.bv-pd-tab-badge { color: var(--brass-hot); }
.bv-pd-tab-badge:not(.bv-pd-tab-on) { background: rgba(210,164,86,0.12); }
.bv-pd-pane { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.bv-pd-pane[hidden] { display: none; }
.bv-pd-slots { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; align-content: start; }
/* Seven slots in two columns: Footwear takes its own row so Primary and Secondary stay a pair. */
/* Eight slots now (VEST, 2026-10-16) pair up evenly in two columns, so FEET no longer spans a row. */
/* The Codex \`.well\` treatment: recessed, hairline-bordered, sunk into the
   panel rather than raised off it. */
.bv-pd-slot {
  border: 1px solid var(--line); border-radius: var(--radius-well); padding: 8px 10px;
  background: var(--well);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.45);
}
.bv-pd-slot-broken { border-color: var(--rust); }
.bv-pd-slot-label {
  font: 700 10px var(--font-technical); text-transform: uppercase;
  letter-spacing: 0.1em; color: var(--bone-dim); margin-bottom: 2px;
}
.bv-pd-slot-item {
  font: 13px/1.3 var(--font-technical); color: var(--bone);
  display: flex; align-items: center; justify-content: space-between; gap: 6px; flex-wrap: wrap;
}
.bv-pd-attrs, .bv-pd-stats, .bv-pd-spec {
  border: 1px solid var(--line); border-radius: var(--radius-well); padding: 10px 12px;
  background: var(--well);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.45);
}
/* Scuffed recess for the slot, stats and spec wells (the attribute block is
   styled separately): speckle, a faint scratch, a darker rim. */
.bv-pd-slot, .bv-pd-stats, .bv-pd-spec {
  border-color: #4a3920;
  background:
    radial-gradient(circle at 16% 22%, rgba(255,235,200,0.07) 0, transparent 1px) 0 0 / 17px 13px,
    radial-gradient(circle at 66% 74%, rgba(0,0,0,0.5) 0, transparent 1px) 0 0 / 23px 19px,
    linear-gradient(112deg, transparent 52%, rgba(255,235,200,0.05) 52.4%, transparent 53.2%),
    radial-gradient(130% 120% at 50% 0%, rgba(110,70,30,0.14), transparent 60%),
    linear-gradient(180deg, #080604, var(--well) 55%, #16100a);
  box-shadow: inset 0 3px 7px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(0,0,0,0.4), 0 1px 0 rgba(255,215,150,0.07);
}
.bv-pd-attrs-head, .bv-pd-stats-head, .bv-pd-spec-head {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  font: 700 11px var(--font-display); text-transform: uppercase;
  letter-spacing: 0.1em; color: var(--brass-bright); margin-bottom: 6px;
}
.bv-pd-points { color: var(--bone-faint); text-transform: none; letter-spacing: 0.02em; }
.bv-pd-points-on { color: var(--brass-hot); text-transform: none; letter-spacing: 0.02em; }
/* One card per attribute: icon, name and note, the number, then the - and +
   buttons. Desktop packs the cards into a grid; phones get a single narrow
   column. The control cell always holds two slots (the minus only exists once
   a point is staged) so the + never slides left on cards with nothing staged. */
.bv-pd-attr-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px;
}
.bv-pd-attr-card {
  display: grid; grid-template-columns: 48px 1fr auto auto; align-items: center; gap: 10px;
  padding: 8px 10px; min-width: 0;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: linear-gradient(180deg, rgba(255,235,200,0.04), transparent 40%), var(--well);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.6), 0 1px 0 rgba(255,215,150,0.06);
  font: 12px/1.3 var(--font-technical); color: var(--brass-bright);
}
.bv-pd-attr-icon { width: 48px; height: 48px; object-fit: contain; display: block; pointer-events: none; }
.bv-pd-attr-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.bv-pd-attr-name { display: flex; align-items: baseline; gap: 6px; font-weight: 700; }
.bv-pd-attr-name em { font-style: normal; font-size: 10px; color: var(--bone-faint); }
.bv-pd-attr-note { font-size: 10px; line-height: 1.25; color: var(--bone-faint); }
.bv-pd-attr-val {
  color: var(--bone); font: 700 20px/1 var(--font-display);
  font-variant-numeric: tabular-nums; min-width: 2ch; text-align: right;
}
.bv-pd-attr-ctl { display: flex; align-items: center; gap: 4px; }
.bv-pd-alloc {
  width: 28px; height: 28px; line-height: 1; cursor: pointer;
  background: linear-gradient(160deg, var(--brass), #8a6a28); color: #1a1208;
  border: 1px solid var(--line); border-radius: var(--radius-tape);
  font: 700 16px/1 var(--font-display);
}
/* Phones: one column of narrower cards, and tap targets at the 44px minimum. */
@media (max-width: 720px) {
  .bv-pd-attr-grid { grid-template-columns: 1fr; max-width: 360px; margin-inline: auto; }
  .bv-pd-attr-card { grid-template-columns: 40px 1fr auto auto; gap: 8px; padding: 6px 8px; }
  .bv-pd-attr-icon { width: 40px; height: 40px; }
  .bv-pd-alloc { width: 44px; height: 44px; font-size: 20px; }
  .bv-pd-alloc-gap { width: 44px; height: 44px; }
}
.bv-pd-alloc:hover:not(:disabled) { background: linear-gradient(160deg, var(--brass-hot), var(--brass)); }
/* Disabled rather than hidden: a player with no points still needs to see that
   spending exists, and where points come from (the button's title says). */
.bv-pd-alloc:disabled { opacity: 0.3; cursor: default; }
/* ── Staged points ────────────────────────────────────────────────────────
   Janeks: *"add skill points and then hit save to apply."* Nothing here is
   spent until Save, so the pending amount reads as a promise beside the real
   number rather than being summed into it. */
.bv-pd-alloc-gap { display: block; width: 28px; height: 28px; }
.bv-pd-unalloc { background: linear-gradient(160deg, #6b5a44, #4a3d2c); color: var(--bone); }
.bv-pd-unalloc:hover:not(:disabled) { background: linear-gradient(160deg, #82705a, #5c4c38); }
.bv-pd-attr-pending {
  margin-left: 4px; font-style: normal; color: var(--moss);
  font: 700 11px/1 var(--font-technical);
}
/* A sweet or a book's timed +1. Brass, not moss: it is a gift that runs out,
   where the moss +N beside it is a point the player is about to spend. */
.bv-pd-attr-buff {
  margin-left: 4px; font-style: normal; color: var(--brass-hot);
  font: 700 11px/1 var(--font-technical);
  text-shadow: 0 0 6px rgba(255,189,92,0.45);
}
.bv-pd-stage-foot {
  display: flex; align-items: center; gap: 6px; margin-top: 8px;
  padding-top: 8px; border-top: 1px solid var(--line);
}
.bv-pd-stage-count {
  flex: 1; color: var(--moss);
  font: 11px var(--font-technical); letter-spacing: 0.04em;
}
.bv-pd-stage-btn { padding: 3px 12px; font-size: 0.76rem; }
.bv-pd-stat-row {
  display: flex; justify-content: space-between;
  font: 12px/1.7 var(--font-technical); color: var(--bone-dim);
}
.bv-pd-stat-row span:last-child { color: var(--bone); font-variant-numeric: tabular-nums; }
.bv-pd-spec-on { font: 13px var(--font-technical); color: var(--bone); }
.bv-pd-spec-off { font: 12px/1.4 var(--font-note); color: var(--bone-faint); }
/* Three capacity meters side by side: carry weight, carried slots, hideout
   slots. Weight and carried slots are co-equal caps (inventory.md rule 3), so
   both carry the same orange/red pressure treatment. */
.bv-pd-inv {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  border: 1px solid var(--line); border-radius: var(--radius-well); padding: 10px 12px;
  background: var(--well); box-shadow: inset 0 1px 2px rgba(0,0,0,0.45);
  font: 12px var(--font-technical); letter-spacing: 0.04em;
}
@media (max-width: 900px) { .bv-pd-inv { grid-template-columns: 1fr; gap: 8px; } }
.bv-pd-cap-head {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  margin-bottom: 4px;
}
/* nowrap on both halves: "Carry weight" + "0.1 / 2.0 kg" is the longest pair,
   and letting either wrap pushes that meter's bar out of line with the other
   two — the three read as one row or they read as broken. */
.bv-pd-cap-head span:first-child {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--bone-dim);
  white-space: nowrap;
}
.bv-pd-cap-head span:last-child {
  color: var(--bone); font-variant-numeric: tabular-nums; font-size: 11px;
  white-space: nowrap;
}
.bv-pd-cap-bar {
  height: 6px; border-radius: var(--radius-tape); overflow: hidden;
  background: var(--well); border: 1px solid var(--line);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.5);
}
.bv-pd-cap-fill { height: 100%; transition: width 0.2s ease, background 0.2s ease; }
/* The Codex \`.callout-danger\` treatment: accent bar on the left rather than a
   full red box, which at this size read as an error page. */
.bv-pd-msg {
  margin: 0 18px; padding: 8px 12px; border-radius: var(--radius-well);
  background: rgba(168,69,44,0.1); border: 1px solid var(--line);
  border-left: 3px solid var(--rust);
  color: var(--bone); font: 12px/1.4 var(--font-technical);
}
.bv-pd-lists { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 900px) { .bv-pd-lists { grid-template-columns: 1fr; } }
.bv-pd-list-label {
  font: 700 11px var(--font-display); text-transform: uppercase;
  letter-spacing: 0.1em; color: var(--brass-bright); margin-bottom: 6px;
}
.bv-pd-items {
  display: flex; flex-direction: column; gap: 6px;
  max-height: 220px; overflow-y: auto;
}
.bv-pd-item-empty { font: 12px var(--font-note); color: var(--bone-faint); padding: 8px 0; }
.bv-pd-item-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  border: 1px solid var(--line); border-radius: var(--radius-well); padding: 6px 10px;
  background: var(--well);
  transition: border-color 0.12s, background 0.12s;
}
.bv-pd-item-row:hover { border-color: var(--brass); background: linear-gradient(180deg, #241710, #17100a); }
.bv-pd-item-name { font: 12px var(--font-technical); color: var(--bone); flex: 1; min-width: 80px; }
.bv-pd-item-weight { font: 11px var(--font-technical); color: var(--bone-dim); font-variant-numeric: tabular-nums; }
/* Item icons: paperdoll.ts has been emitting these <img>s since S6 with no
   rule to size them, so they rendered at the PNG's natural pixel size. */
.bv-pd-item-icon, .bv-pd-slot-icon {
  width: 26px; height: 26px; flex: 0 0 auto; object-fit: contain;
  image-rendering: -webkit-optimize-contrast;
  border-radius: var(--radius-tape); background: #0d0906;
  border: 1px solid var(--line);
}
.bv-pd-slot-icon { width: 22px; height: 22px; }
.bv-pd-slot-item { gap: 8px; }
/* Per-piece armour points, in the same steel tone as the HUD's AR bar. */
.bv-pd-slot-armor {
  font: 700 10px var(--font-technical); color: #8fb0c4;
  letter-spacing: 0.06em; white-space: nowrap;
  border: 1px solid rgba(143,176,196,0.35); border-radius: var(--radius-tape); padding: 1px 5px;
}
/* Broken gear's contribution is scaled by brokenFloor server-side, so the raw
   number shown here is no longer what it is worth — dim it to say so. */
.bv-pd-slot-broken .bv-pd-slot-armor { color: #6c7d88; border-color: rgba(108,125,136,0.3); opacity: 0.65; }
.bv-pd-item-actions { display: flex; gap: 4px; margin-left: auto; }
.bv-pd-btn {
  font: 700 11px var(--font-display); text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--brass-bright); background: var(--well); border: 1px solid var(--line);
  border-radius: var(--radius-tape);
  padding: 4px 8px; cursor: pointer; transition: border-color 0.12s, color 0.12s;
}
.bv-pd-btn:hover { border-color: var(--brass); color: var(--brass-hot); }
.bv-pd-btn-danger { color: var(--rust); }
.bv-pd-btn-danger:hover { border-color: var(--rust); color: #e07a5f; }

/* ══ Depth pass (2026-10-13) ════════════════════════════════════════════════
   Everything above is the flat layout; everything below is the surface. It
   sits at the END on purpose: same specificity as the rules it refines, so
   source order is what makes it win. Delete this block and the panel is still
   correct, just flat. Item slots, load meters and the tooltip get the same
   treatment in item.css.ts.

   Motion rules, so the panel is alive without being noisy:
   - idle loops are slow (4-9s) and low amplitude, and transform/opacity only,
     so they cost compositor time, not layout;
   - every hover is a lift (translate) plus a light change, never a size
     change that reflows a neighbour;
   - touch devices never need hover: the idle layer carries the life there;
   - prefers-reduced-motion switches every loop and transition off at the foot
     of this block. */

/* Open: scrim fades, panel settles into place. Replays each time display goes
   from none to flex, which is exactly when the Record opens. */
@keyframes bvpd-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes bvpd-rise {
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  60%  { opacity: 1; }
  to   { opacity: 1; transform: none; }
}
@keyframes bvpd-lamp {
  0%, 100% { opacity: 0.55; box-shadow: 0 0 3px 0 rgba(255,150,40,0.35); }
  50%      { opacity: 1;    box-shadow: 0 0 9px 2px rgba(255,170,60,0.85); }
}
@keyframes bvpd-glint {
  0%, 62% { transform: translateX(-130%); }
  100%    { transform: translateX(330%); }
}
@keyframes bvpd-breathe {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,189,92,0), inset 0 0 0 rgba(255,189,92,0); }
  50%      { box-shadow: 0 0 10px 1px rgba(255,189,92,0.38), inset 0 0 8px rgba(255,189,92,0.12); }
}
@keyframes bvpd-bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}
@keyframes bvpd-hint {
  0%, 100% { opacity: 0.35; } 50% { opacity: 0.85; }
}
@keyframes bvpd-shimmer {
  from { transform: translateX(-100%); } to { transform: translateX(100%); }
}
@keyframes bvpd-alarm {
  0%, 100% { filter: brightness(1); } 50% { filter: brightness(1.45); }
}

.bv-paperdoll { animation: bvpd-fade 0.18s ease-out; }
.bv-pd-panel {
  position: relative;
  border: 2px solid #120d08; border-radius: var(--radius-panel);
  background:
    /* bottom rivets (the header carries its own) */
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 9px bottom 9px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 9px bottom 9px / 10px 10px no-repeat,
    /* rust runs down from the top edge, two of them, not symmetrical */
    linear-gradient(180deg, rgba(150,66,26,0.26), transparent) 6% 0 / 22px 210px no-repeat,
    linear-gradient(180deg, rgba(130,56,22,0.2), transparent) 83% 0 / 34px 150px no-repeat,
    linear-gradient(180deg, rgba(150,66,26,0.14), transparent) 41% 0 / 14px 90px no-repeat,
    /* corner burn and a worn-bright middle */
    radial-gradient(130% 90% at 50% 0%, rgba(150,100,45,0.13), transparent 58%),
    radial-gradient(90% 70% at 0% 100%, rgba(0,0,0,0.5), transparent 70%),
    radial-gradient(90% 70% at 100% 100%, rgba(0,0,0,0.5), transparent 70%),
    /* scratches and speckle */
    linear-gradient(104deg, transparent 47%, rgba(255,235,200,0.05) 47.3%, transparent 48.1%) 0 0 / 410px 260px,
    linear-gradient(78deg, transparent 61%, rgba(0,0,0,0.3) 61.2%, transparent 61.9%) 0 0 / 330px 210px,
    radial-gradient(circle at 18% 24%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 19px 15px,
    radial-gradient(circle at 70% 76%, rgba(0,0,0,0.45) 0 1px, transparent 1.6px) 0 0 / 27px 21px,
    linear-gradient(165deg, var(--panel-a), var(--panel-b));
  box-shadow:
    0 0 0 1px #5a4326,
    0 28px 64px rgba(0,0,0,0.78), 0 5px 0 rgba(0,0,0,0.55),
    inset 0 2px 0 rgba(255,222,170,0.2), inset 0 -3px 0 rgba(0,0,0,0.6),
    inset 0 0 0 5px rgba(171,138,83,0.09), inset 0 0 70px rgba(0,0,0,0.55);
  animation: bvpd-rise 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.1);
}
.bv-pd-header {
  position: relative; overflow: hidden;
  padding: 14px 54px 14px 26px;
  background:
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 8px center / 10px 10px no-repeat,
    repeating-linear-gradient(135deg, #17100a 0 7px, #b8862e 7px 14px) 0 100% / 100% 3px no-repeat,
    repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 3px),
    linear-gradient(180deg, #55402a 0%, #382818 52%, #271b10 100%);
  border-bottom: 1px solid #0e0a06;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.3), inset 0 -4px 0 rgba(0,0,0,0.25), 0 3px 8px rgba(0,0,0,0.55);
  text-shadow: 0 1px 0 #000, 0 0 14px rgba(255,189,92,0.28);
}
/* A slow band of light crossing the plate. Behind the text, over the metal. */
.bv-pd-header::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 28%;
  background: linear-gradient(100deg, transparent, rgba(255,226,170,0.16), transparent);
  pointer-events: none; animation: bvpd-glint 8s ease-in-out infinite;
}
/* The status lamp: a bezel with an amber LED that breathes. */
.bv-pd-header::after {
  content: ""; position: absolute; top: 50%; right: 74px; width: 9px; height: 9px;
  margin-top: -4.5px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff1c9 0 1px, #ffb04a 2px, #b4601a 100%);
  border: 1px solid #120d08; outline: 2px solid #4a3920;
  animation: bvpd-lamp 3.4s ease-in-out infinite;
}

/* The survivor viewport: bolted frame and corner plates. The hint breathes so the box reads as live, not a still. */
.bv-pd-preview {
  border: 2px solid #120d08;
  box-shadow: 0 0 0 1px #5a4326, inset 0 0 34px rgba(0,0,0,0.78), inset 0 0 0 3px rgba(171,138,83,0.12),
              0 3px 10px rgba(0,0,0,0.55);
}
.bv-pd-preview::before {
  content: ""; position: absolute; inset: 0; z-index: 2; pointer-events: none;
  background:
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #120d08 3.4px, transparent 4px) left 5px top 5px / 8px 8px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #120d08 3.4px, transparent 4px) right 5px top 5px / 8px 8px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #120d08 3.4px, transparent 4px) left 5px bottom 5px / 8px 8px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #120d08 3.4px, transparent 4px) right 5px bottom 5px / 8px 8px no-repeat,
    repeating-linear-gradient(180deg, rgba(0,0,0,0.12) 0 1px, transparent 1px 3px);
}
.bv-pd-preview-hint { z-index: 3; animation: bvpd-hint 3.6s ease-in-out infinite; }

/* Tabs are metal tags now: they lift on hover and the active one is bolted
   forward. The unspent-points badge breathes, since it is the one tab that
   should pull the eye. */
.bv-pd-tabs { border-bottom: 2px solid #120d08; box-shadow: 0 1px 0 rgba(255,215,150,0.1); }
.bv-pd-tab {
  position: relative; text-shadow: 0 1px 0 rgba(0,0,0,0.8);
  background: linear-gradient(180deg, #2a2014, #150f09);
  border-color: #3a2c19;
  transition: transform 0.14s ease, color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
}
.bv-pd-tab:hover {
  transform: translateY(-2px); color: var(--brass-hot);
  background: linear-gradient(180deg, #3d2d1b, #1b130b);
  box-shadow: 0 -3px 8px rgba(255,189,92,0.14);
}
.bv-pd-tab:active { transform: translateY(0); }
.bv-pd-tab:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: -2px; }
.bv-pd-tab-on {
  background: linear-gradient(180deg, #4a3822, #1d140b 70%);
  border-color: #6b5230; border-top: 2px solid var(--brass-hot);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.25), 0 -4px 10px rgba(255,189,92,0.12);
  transform: translateY(-1px);
}
.bv-pd-tab-badge:not(.bv-pd-tab-on) { animation: bvpd-breathe 2.6s ease-in-out infinite; }

/* Wells: raised-on-hover so a stat block or a worn slot reads as a thing you
   can look at, with the brass edge arriving rather than snapping. */
.bv-pd-slot, .bv-pd-stats, .bv-pd-spec, .bv-pd-attrs {
  transition: transform 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease;
}
.bv-pd-slot:hover {
  transform: translateY(-2px); border-color: var(--brass);
  box-shadow: inset 0 3px 7px rgba(0,0,0,0.8), 0 0 12px rgba(255,189,92,0.18), 0 4px 8px rgba(0,0,0,0.55);
}
.bv-pd-slot-icon { transition: transform 0.18s cubic-bezier(0.3, 1.6, 0.5, 1); box-shadow: inset 0 1px 3px rgba(0,0,0,0.8); }
.bv-pd-slot:hover .bv-pd-slot-icon { transform: scale(1.18) rotate(-4deg); }
.bv-pd-slot-label { text-shadow: 0 1px 0 rgba(0,0,0,0.9); }
.bv-pd-slot-armor { background: rgba(60,84,98,0.25); box-shadow: inset 0 1px 2px rgba(0,0,0,0.5); }
.bv-pd-attrs-head, .bv-pd-stats-head, .bv-pd-spec-head, .bv-pd-list-label {
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}
.bv-pd-spec-on { text-shadow: 0 0 10px rgba(255,189,92,0.35); }

/* Skills tab: each attribute is a plate. The icon drifts on its own, out of
   step with its neighbours, and snaps forward when the plate is touched. */
.bv-pd-attr-card {
  position: relative;
  background:
    radial-gradient(circle, #e8c98f 0 0.9px, #6b4e22 1.8px, transparent 2.6px) left 4px top 4px / 6px 6px no-repeat,
    radial-gradient(circle, #e8c98f 0 0.9px, #6b4e22 1.8px, transparent 2.6px) right 4px top 4px / 6px 6px no-repeat,
    radial-gradient(circle, #e8c98f 0 0.9px, #6b4e22 1.8px, transparent 2.6px) left 4px bottom 4px / 6px 6px no-repeat,
    radial-gradient(circle, #e8c98f 0 0.9px, #6b4e22 1.8px, transparent 2.6px) right 4px bottom 4px / 6px 6px no-repeat,
    radial-gradient(circle at 24% 28%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 13px 11px,
    linear-gradient(180deg, rgba(255,235,200,0.07), transparent 42%),
    linear-gradient(180deg, #1d150d, #0d0a06);
  border-color: #4a3920;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.7), 0 1px 0 rgba(255,215,150,0.08), 0 2px 5px rgba(0,0,0,0.4);
  transition: transform 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease;
}
.bv-pd-attr-card:hover {
  transform: translateY(-2px); border-color: var(--brass);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.7), 0 0 14px rgba(255,189,92,0.2), 0 5px 10px rgba(0,0,0,0.55);
}
.bv-pd-attr-icon {
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.8));
  animation: bvpd-bob 4.6s ease-in-out infinite;
  transition: transform 0.2s cubic-bezier(0.3, 1.6, 0.5, 1), filter 0.15s ease;
}
.bv-pd-attr-card:nth-child(2n) .bv-pd-attr-icon { animation-delay: -1.1s; }
.bv-pd-attr-card:nth-child(3n) .bv-pd-attr-icon { animation-delay: -2.3s; }
.bv-pd-attr-card:nth-child(5n) .bv-pd-attr-icon { animation-delay: -3.4s; }
.bv-pd-attr-card:hover .bv-pd-attr-icon {
  animation-play-state: paused; transform: scale(1.14) rotate(-5deg);
  filter: drop-shadow(0 0 7px rgba(255,189,92,0.55));
}
.bv-pd-attr-val { text-shadow: 0 1px 0 #000, 0 0 10px rgba(255,236,200,0.14); }
.bv-pd-alloc {
  border-color: #3d2a0e;
  background: linear-gradient(180deg, #e4b868, var(--brass) 45%, #7d5c22);
  box-shadow: inset 0 1px 0 rgba(255,246,222,0.5), inset 0 -2px 0 rgba(70,44,10,0.55), 0 2px 3px rgba(0,0,0,0.6);
  transition: transform 0.1s ease, filter 0.12s ease, box-shadow 0.12s ease;
}
.bv-pd-alloc:hover:not(:disabled) { transform: translateY(-1px) scale(1.08); filter: brightness(1.15); }
.bv-pd-alloc:active:not(:disabled) {
  transform: translateY(1px) scale(0.96);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.6), 0 0 0 rgba(0,0,0,0);
}
.bv-pd-alloc:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }
.bv-pd-unalloc { background: linear-gradient(180deg, #86735b, #4a3d2c 55%, #30271b); }
.bv-pd-points-on { animation: bvpd-alarm 1.8s ease-in-out infinite; text-shadow: 0 0 9px rgba(255,189,92,0.55); }

/* Meters: a recessed groove with graduation ticks, and a fill that carries a
   slow highlight. Critical fill pulses harder. The sheen is a pseudo-element
   so the fill's own width transition is untouched. */
.bv-pd-cap-bar {
  position: relative; height: 8px; border-color: #4a3920;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.85), 0 1px 0 rgba(255,215,150,0.1);
}
.bv-pd-cap-fill { position: relative; overflow: hidden; box-shadow: inset 0 1px 0 rgba(255,255,255,0.28); }
.bv-pd-cap-fill::after, .bv-load-fill::after {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%);
  animation: bvpd-shimmer 3.8s ease-in-out infinite;
}
.bv-cap-critical .bv-pd-cap-fill, .bv-cap-critical .bv-load-fill { animation: bvpd-alarm 1.2s ease-in-out infinite; }

/* Messages and item rows. */
.bv-pd-msg { box-shadow: inset 0 1px 3px rgba(0,0,0,0.5), 0 2px 5px rgba(0,0,0,0.4); }
.bv-pd-item-row { box-shadow: inset 0 1px 3px rgba(0,0,0,0.55); transition: border-color 0.12s, background 0.12s, transform 0.12s; }
.bv-pd-item-row:hover { transform: translateX(3px); }
.bv-pd-btn {
  background: linear-gradient(180deg, #3d2f1c, #1c1409);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.18), 0 1px 3px rgba(0,0,0,0.55);
  transition: border-color 0.12s, color 0.12s, transform 0.1s, filter 0.12s;
}
.bv-pd-btn:hover { transform: translateY(-1px); filter: brightness(1.2); }
.bv-pd-btn:active { transform: translateY(1px); filter: brightness(0.9); }

@media (prefers-reduced-motion: reduce) {
  .bv-paperdoll, .bv-pd-panel, .bv-pd-header::before, .bv-pd-header::after,
  .bv-pd-preview-hint, .bv-pd-tab-badge,
  .bv-pd-attr-icon, .bv-pd-points-on, .bv-pd-cap-fill, .bv-pd-cap-fill::after,
  .bv-load-fill::after, .bv-load-fill { animation: none; }
  .bv-pd-tab, .bv-pd-slot, .bv-pd-slot-icon, .bv-pd-attr-card,
  .bv-pd-attr-icon, .bv-pd-alloc, .bv-pd-item-row, .bv-pd-btn { transition: none; }
  .bv-pd-tab:hover, .bv-pd-slot:hover, .bv-pd-attr-card:hover,
  .bv-pd-alloc:hover:not(:disabled), .bv-pd-item-row:hover, .bv-pd-btn:hover { transform: none; }
}

/* ══ Skills tab: locked things (2026-10-13) ═════════════════════════════════
   Two disabled surfaces, both taped off: the sixth skill card (a placeholder
   for an attribute coming in a future update) and the Specialization well
   while it is locked. Hazard tape is one recipe, used for both.

   The lower row: With gear takes a third, Specialization the other two. */
.bv-pd-lower { display: grid; grid-template-columns: 1fr 2fr; gap: 12px; align-items: stretch; }
@media (max-width: 720px) { .bv-pd-lower { grid-template-columns: 1fr; } }
.bv-pd-lower > .bv-pd-stats, .bv-pd-lower > .bv-pd-spec { min-width: 0; }

/* The hazard stripe: yellow and black at 45 degrees to the tape's length, with
   a worn overlay so it is not a clean vector. Used by every tape piece. */
.bv-pd-tape, .bv-pd-spec-tape {
  background:
    radial-gradient(circle at 20% 40%, rgba(255,255,255,0.18) 0 1px, transparent 1.6px) 0 0 / 11px 9px,
    radial-gradient(circle at 70% 70%, rgba(0,0,0,0.35) 0 1px, transparent 1.6px) 0 0 / 13px 10px,
    linear-gradient(180deg, rgba(255,255,255,0.22), transparent 30%, rgba(0,0,0,0.3)),
    repeating-linear-gradient(45deg, #e0ad24 0 9px, #17110a 9px 18px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(0,0,0,0.35);
}

/* Specialization, locked: a dimmed, dead plate with one band of tape across
   it. The Locked line is printed on a dark label INSIDE the band, so it is
   rotated with the band and follows the tape's line. */
.bv-pd-spec { position: relative; overflow: hidden; display: flex; flex-direction: column; min-height: 118px; }
.bv-pd-spec-locked {
  background:
    radial-gradient(circle at 16% 22%, rgba(255,235,200,0.05) 0 1px, transparent 1.5px) 0 0 / 17px 13px,
    linear-gradient(135deg, rgba(120,52,22,0.3), transparent 45%, rgba(0,0,0,0.5)),
    linear-gradient(180deg, #120d08, #0a0705);
  border-color: #3a2616;
}
.bv-pd-spec-locked .bv-pd-spec-head { opacity: 0.45; filter: grayscale(0.7); }
.bv-pd-spec-locked:hover { transform: none; border-color: #3a2616; box-shadow: inset 0 3px 7px rgba(0,0,0,0.8), 0 1px 0 rgba(255,215,150,0.07); }
.bv-pd-spec-tape {
  position: absolute; left: -5%; width: 110%; top: 56%; height: 22px;
  transform: translateY(-50%) rotate(-3.2deg);
  display: flex; align-items: center; justify-content: center;
}
.bv-pd-spec-tape .bv-pd-spec-off {
  padding: 1px 12px; max-width: 80%;
  background: #14100b; color: var(--brass-bright);
  border: 1px solid #000; border-radius: var(--radius-tape);
  font: 700 10px/1.4 var(--font-technical); letter-spacing: 0.12em; text-transform: uppercase;
  text-align: center; text-shadow: 0 1px 0 #000; white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.7);
}

/* The sixth skill card: corroded plate from the generated texture, contents
   dimmed, no interaction. Overflow stays VISIBLE so the loose tape can hang
   off the bottom edge. */
.bv-pd-attr-locked {
  position: relative; grid-template-columns: 1fr; min-height: 84px; cursor: not-allowed;
  /* Trim the tape at the card's sides only; it may overhang top and bottom. */
  clip-path: inset(-120px 0 -120px 0);
  background:
    linear-gradient(180deg, rgba(8,5,3,0.5), rgba(8,5,3,0.68)),
    url(/icons/ui/skill-locked-plate.webp) center / cover no-repeat;
  border-color: #4a2a18;
  box-shadow: inset 0 0 22px rgba(0,0,0,0.75), inset 0 0 0 1px rgba(0,0,0,0.5), 0 2px 5px rgba(0,0,0,0.5);
}
.bv-pd-attr-locked:hover {
  transform: none; border-color: #4a2a18;
  box-shadow: inset 0 0 22px rgba(0,0,0,0.75), inset 0 0 0 1px rgba(0,0,0,0.5), 0 2px 5px rgba(0,0,0,0.5);
}

/* Tape pieces. Every piece is pinned by its left end and rotated about it.
   Stuck A runs corner to corner top-left to bottom-right; stuck B comes up
   from the bottom-left and stops at the middle, where the rest of that band
   has peeled away and hangs down. */
.bv-pd-tape { position: absolute; height: 14px; pointer-events: none; display: block; }
.bv-pd-tape-stuck-a {
  left: -2%; top: 6px; width: 104%;
  transform-origin: left center; transform: rotate(17deg);
}
.bv-pd-tape-stuck-b {
  left: -2%; top: calc(100% - 14px); width: 53%;
  transform-origin: left center; transform: rotate(-17deg);
  /* the end that lets go is torn rather than cut */
  clip-path: polygon(0 0, 100% 0, 96% 22%, 100% 42%, 95% 62%, 99% 80%, 94% 100%, 0 100%);
}
/* The loose end. The outer span is the hinge at the middle of the card and
   swings slowly; the inner piece is the tape itself, which flutters on its own
   faster beat, so the two never line up and the motion reads as wind rather
   than a pendulum. */
.bv-pd-tape-fall {
  position: absolute; left: 50%; top: 50%; width: 0; height: 0; z-index: 3;
  transform-origin: 0 0; transform: rotate(78deg);
  animation: bvpd-tape-sway 5.7s ease-in-out infinite;
}
.bv-pd-tape-loose {
  left: 0; top: -7px; width: 58px; height: 14px;
  transform-origin: left center;
  clip-path: polygon(0 0, 100% 0, 95% 20%, 100% 38%, 94% 58%, 99% 78%, 92% 100%, 0 100%);
  filter: brightness(0.92) drop-shadow(0 3px 2px rgba(0,0,0,0.6));
  animation: bvpd-tape-flutter 2.3s ease-in-out infinite;
}
@keyframes bvpd-tape-sway {
  0%, 100% { transform: rotate(74deg); }
  35%      { transform: rotate(84deg); }
  62%      { transform: rotate(71deg); }
}
@keyframes bvpd-tape-flutter {
  0%, 100% { transform: rotate(0deg) skewY(0deg) scaleX(1); }
  30%      { transform: rotate(7deg) skewY(-4deg) scaleX(0.96); }
  55%      { transform: rotate(-4deg) skewY(3deg) scaleX(1.02); }
  80%      { transform: rotate(5deg) skewY(-2deg) scaleX(0.98); }
}
@media (prefers-reduced-motion: reduce) {
  .bv-pd-tape-fall, .bv-pd-tape-loose { animation: none; }
}

/* ══ Inventory layout, 2026-10-13 ═══════════════════════════════════════════
   Worn gear sits under the preview, so the right-hand side is the bag and the
   stash, 6 across by 5 down each. The panel is wider to give them the room. */
.bv-pd-panel { width: min(1280px, 96%); }
.bv-pd-worn { padding: 8px 10px 10px; }
.bv-pd-worn .bv-block-head { margin-bottom: 6px; padding-bottom: 4px; }
.bv-pd-worn .bv-pd-slots { gap: 6px; }
/* Block-title pictures: a backpack for the bag, a chest for the stash. */
.bv-block-title { display: inline-flex; align-items: center; gap: 8px; }
.bv-block-icon {
  width: 30px; height: 30px; margin: -8px 0; flex: none;
  object-fit: contain; pointer-events: none;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.7));
  animation: bvpd-bob 5.2s ease-in-out infinite;
  transition: transform 0.2s cubic-bezier(0.3, 1.6, 0.5, 1);
}
.bv-block:hover .bv-block-icon { animation-play-state: paused; transform: scale(1.18) rotate(-6deg); }
@media (prefers-reduced-motion: reduce) { .bv-block-icon { animation: none; transition: none; } }

/* Six attribute cards (five plus the placeholder) fill a 3 x 2 block exactly. */
.bv-pd-attr-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
@media (max-width: 720px) { .bv-pd-attr-grid { grid-template-columns: 1fr; } }

/* Worn gear, 2026-10-14: icons only, no frame. Name, condition and stats are in
   the hover tooltip. The label stays as a tiny caption so empty slots read. */
.bv-pd-worn .bv-pd-slots { grid-template-columns: repeat(4, 1fr); gap: 4px; }
.bv-pd-worn .bv-pd-slots > [data-tip="eq:FEET"] { grid-column: auto; }
.bv-pd-worn .bv-pd-slot {
  position: relative; border: 0; background: none; box-shadow: none; padding: 2px 0 0;
  display: flex; flex-direction: column; align-items: center; gap: 2px; min-height: 76px;
}
.bv-pd-worn .bv-pd-slot:hover { border: 0; box-shadow: none; transform: none; }
.bv-pd-worn .bv-pd-slot-item { min-height: 56px; width: 56px; justify-content: center; flex-wrap: nowrap; }
.bv-pd-worn .bv-pd-slot-icon { width: 56px; height: 56px; border: 0; background: none; box-shadow: none; border-radius: 0; filter: drop-shadow(0 2px 2px rgba(0,0,0,0.7)); }
.bv-pd-worn .bv-pd-slot-label { order: 2; margin: 0; font-size: 9px; }
.bv-pd-slot-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.bv-pd-worn .bv-pd-slot-broken .bv-pd-slot-icon { filter: grayscale(0.8) drop-shadow(0 0 3px var(--rust)); }
.bv-pd-unequip {
  position: absolute; top: 0; right: 2px; width: 18px; height: 18px; padding: 0; line-height: 16px;
  border: 1px solid var(--line); border-radius: 50%; background: #0d0906; color: var(--bone-dim);
  font: 700 12px var(--font-technical); cursor: pointer; opacity: 0.55; transition: opacity 0.12s, color 0.12s;
}
.bv-pd-slot:hover .bv-pd-unequip, .bv-pd-unequip:focus-visible { opacity: 1; color: var(--rust); border-color: var(--rust); }

/* Stats: Stat / Base / Gear / Total. */
.bv-pd-stats .bv-pd-stat-row { display: grid; grid-template-columns: minmax(0, 1.5fr) repeat(3, minmax(0, 1fr)); gap: 4px; text-align: right; }
.bv-pd-stats .bv-pd-stat-row span:first-child { text-align: left; }
.bv-pd-stat-cols { font: 700 9px var(--font-technical); text-transform: uppercase; letter-spacing: 0.1em; color: var(--bone-faint); border-bottom: 1px solid var(--line); }
.bv-pd-stat-row span:last-child { font-weight: 700; }
.bv-pd-stat-row .bv-pd-gear { color: var(--bone-faint); }
.bv-pd-stat-row .bv-pd-gear-up { color: var(--moss); }
.bv-pd-stat-row .bv-pd-gear-down { color: var(--rust); }

/* Dragging an item: the worn slot that takes it lights up and breathes; the
   rest of the doll steps back. */
.bv-drag-ghost {
  position: fixed; left: 0; top: 0; z-index: 1300; width: 64px; height: 64px; margin: -32px 0 0 -32px;
  pointer-events: none; transform: rotate(-6deg) scale(1.08); opacity: 0.92;
  filter: drop-shadow(0 8px 10px rgba(0,0,0,0.7));
}
.bv-drag-ghost img { width: 100%; height: 100%; object-fit: contain; }
.bv-slot-dragging { opacity: 0.35; }
/* Item art is never a native drag source: the pointer drag owns the gesture. */
.bv-pd-items .bv-slot-filled { user-select: none; -webkit-user-select: none; touch-action: manipulation; }
.bv-pd-items .bv-slot-filled img { -webkit-user-drag: none; user-drag: none; pointer-events: none; }
/* The pool a dragged item can be moved into pulses like the worn slot does. */
.bv-pd-pool-target { outline: 2px dashed var(--brass); outline-offset: 2px; animation: bvpd-pool-pulse 1s ease-in-out infinite; }
.bv-pd-pool-over { outline-style: solid; outline-color: var(--brass-hot); background: rgba(255,189,92,0.12); }
@keyframes bvpd-pool-pulse { 0%, 100% { outline-color: var(--brass); } 50% { outline-color: var(--brass-hot); } }
@media (prefers-reduced-motion: reduce) { .bv-pd-pool-target { animation: none; } }
.bv-pd-dragging .bv-pd-slot:not(.bv-pd-slot-target) { opacity: 0.4; filter: saturate(0.5); transition: opacity 0.15s; }
.bv-pd-slot-target {
  border-radius: var(--radius-well);
  background: radial-gradient(circle at 50% 45%, rgba(255,189,92,0.22), transparent 70%);
  animation: bvpd-target 0.9s ease-in-out infinite;
}
.bv-pd-slot-target .bv-pd-slot-label { color: var(--brass-hot); }
.bv-pd-slot-target .bv-pd-slot-icon { animation: bvpd-target-bob 0.9s ease-in-out infinite; }
.bv-pd-slot-over { background: radial-gradient(circle at 50% 45%, rgba(255,189,92,0.42), transparent 75%); outline: 2px dashed var(--brass-hot); outline-offset: -2px; }
@keyframes bvpd-target {
  0%, 100% { box-shadow: 0 0 0 1px rgba(255,189,92,0.35), 0 0 6px rgba(255,189,92,0.15); }
  50%      { box-shadow: 0 0 0 2px rgba(255,189,92,0.9), 0 0 18px rgba(255,189,92,0.5); }
}
@keyframes bvpd-target-bob { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-3px) scale(1.1); } }
@media (prefers-reduced-motion: reduce) { .bv-pd-slot-target, .bv-pd-slot-target .bv-pd-slot-icon { animation: none; } }

/* ══ Smaller preview, worn gear 5 x 2 (Janeks, 2026-10-19) ══════════════════
   The preview drops from 3:4 to square so the worn block fits under it, and
   the nine slots sit in two rows of five instead of 4 + 4 + a lone Backpack.
   The column is 20px wider so five 46px icons and their captions fit. */
.bv-pd-body { grid-template-columns: 280px 1fr; }
@media (max-width: 720px) { .bv-pd-body { grid-template-columns: 1fr; } }
.bv-pd-preview { aspect-ratio: 1 / 1; }
@media (max-width: 720px) { .bv-pd-preview { width: min(100%, 280px); margin-inline: auto; } }
/* A landscape phone is ~390px tall: a short preview keeps the worn gear on screen. */
@media (max-height: 520px) { .bv-pd-preview { aspect-ratio: auto; height: 34vh; min-height: 120px; } }
.bv-pd-worn .bv-pd-slots { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 4px 2px; }
.bv-pd-worn .bv-pd-slot { min-height: 64px; }
.bv-pd-worn .bv-pd-slot-item { min-height: 46px; width: 46px; }
.bv-pd-worn .bv-pd-slot-icon { width: 46px; height: 46px; }
.bv-pd-worn .bv-pd-slot-label { font-size: 8px; letter-spacing: 0.02em; white-space: nowrap; }
.bv-pd-worn .bv-pd-unequip { width: 16px; height: 16px; line-height: 14px; font-size: 11px; right: 0; }
/* The unequip cross shows on hover only (Janeks, 2026-10-19). Touch has no
   hover, so there it stays visible: it is the only way to take a piece off
   without dragging. */
@media (hover: hover) {
  .bv-pd-worn .bv-pd-unequip { opacity: 0; pointer-events: none; }
  .bv-pd-worn .bv-pd-slot:hover .bv-pd-unequip,
  .bv-pd-worn .bv-pd-unequip:focus-visible { opacity: 1; pointer-events: auto; }
}
`;
